import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Biotech as MolecularIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  PlayArrow as AnalyzeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ================================================================
// API Configuration
// ================================================================
const API_URL =
  process.env.REACT_APP_API_URL ||
  'http://localhost:8000';

const API_KEY =
  process.env.REACT_APP_API_KEY ||
  'test-key-123';

// ================================================================
// MOLECULAR FEATURES
// ================================================================
const MOLECULAR_FEATURES = [
  {
    key: 'mtb_detected',
    label: 'MTB Detected',
    type: 'select',
    defaultValue: 0,
    options: [
      { value: 0, label: 'Not Detected' },
      { value: 1, label: 'Detected' },
    ],
  },
  {
    key: 'ct_value',
    label: 'Ct Value',
    type: 'number',
    defaultValue: 25,
    min: 0,
    max: 50,
    step: 0.1,
  },
  {
    key: 'rifampicin_resistance',
    label: 'Rifampicin Resistance',
    type: 'select',
    defaultValue: 0,
    options: [
      { value: 0, label: 'Not Detected' },
      { value: 1, label: 'Detected' },
      { value: 2, label: 'Indeterminate' },
    ],
  },
  {
    key: 'bacterial_load',
    label: 'Bacterial Load',
    type: 'select',
    defaultValue: 0,
    options: [
      { value: 0, label: 'Very Low / None' },
      { value: 1, label: 'Low' },
      { value: 2, label: 'Medium' },
      { value: 3, label: 'High' },
    ],
  },
  {
    key: 'specimen_quality',
    label: 'Specimen Quality',
    type: 'select',
    defaultValue: 1,
    options: [
      { value: 0, label: 'Poor' },
      { value: 1, label: 'Acceptable' },
      { value: 2, label: 'Good' },
    ],
  },
  {
    key: 'isoniazid_resistance',
    label: 'Isoniazid Resistance',
    type: 'select',
    defaultValue: 0,
    options: [
      { value: 0, label: 'Not Detected' },
      { value: 1, label: 'Detected' },
      { value: 2, label: 'Not Tested' },
    ],
  },
  {
    key: 'fluoroquinolone_resistance',
    label: 'Fluoroquinolone Resistance',
    type: 'select',
    defaultValue: 0,
    options: [
      { value: 0, label: 'Not Detected' },
      { value: 1, label: 'Detected' },
      { value: 2, label: 'Not Tested' },
    ],
  },
  {
    key: 'mutation_count',
    label: 'Detected Mutation Count',
    type: 'number',
    defaultValue: 0,
    min: 0,
    max: 100,
    step: 1,
  },
];

function createInitialFeatures() {
  return Object.fromEntries(
    MOLECULAR_FEATURES.map((feature) => [
      feature.key,
      feature.defaultValue,
    ])
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`molecular-tabpanel-${index}`}
      aria-labelledby={`molecular-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function parseCsvLine(line) {
  return line
    .split(',')
    .map((value) => value.trim().replace(/^"|"$/g, ''));
}

const MolecularAnalysis = () => {
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  const [inputMode, setInputMode] = useState('manual');

  const [patientId, setPatientId] = useState('');
  const [features, setFeatures] = useState(createInitialFeatures);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileRows, setFileRows] = useState([]);
  const [fileHeaders, setFileHeaders] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [batchResults, setBatchResults] = useState([]);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);

  const addLog = useCallback((message) => {
    setLogs((previous) => [
      ...previous,
      {
        timestamp: new Date().toLocaleTimeString(),
        message,
      },
    ]);
  }, []);

  const handleFeatureChange = useCallback((key, rawValue) => {
    const value = Number(rawValue);
    setFeatures((previous) => ({
      ...previous,
      [key]: Number.isNaN(value) ? 0 : value,
    }));
  }, []);

  const manualFeatureValues = useMemo(
    () => MOLECULAR_FEATURES.map((feature) => features[feature.key]),
    [features]
  );

  const handleFileSelection = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      setError(null);
      setResult(null);
      setBatchResults([]);
      setFileRows([]);
      setFileHeaders([]);

      if (!file) {
        setSelectedFile(null);
        return;
      }

      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension !== 'csv') {
        setError('Please upload a CSV file.');
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      try {
        const text = await file.text();
        const lines = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        if (lines.length < 2) {
          throw new Error('CSV must contain a header and at least one row.');
        }

        const headers = parseCsvLine(lines[0]);
        const rows = lines.slice(1).map((line) => {
          const values = parseCsvLine(line);
          return Object.fromEntries(
            headers.map((header, columnIndex) => [
              header,
              values[columnIndex] ?? '',
            ])
          );
        });

        setSelectedFile(file);
        setFileHeaders(headers);
        setFileRows(rows);

        addLog(`📄 Loaded ${file.name}`);
        addLog(`📊 Parsed ${rows.length} record(s)`);
      } catch (fileError) {
        setSelectedFile(null);
        setError(fileError.message || 'Unable to parse CSV file.');
        addLog(`❌ File error: ${fileError.message}`);
      }
    },
    [addLog]
  );

  const handleManualAnalyze = useCallback(async () => {
    if (!patientId.trim()) {
      setError('Please enter a Patient ID.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgress(10);

    addLog('🚀 Starting molecular analysis...');

    // ================================================================
    // DEBUG: Log the URL being called
    // ================================================================
    const molecularUrl = `${API_URL}/v1/predict/molecular/`;
    alert("Calling: " + molecularUrl);
    const payload = {
      patient_id: patientId.trim(),
      features: manualFeatureValues,
      feature_names: MOLECULAR_FEATURES.map(
        (feature) => feature.key
      ),
    };

    console.log('===== MOLECULAR REQUEST =====');
    console.log('API_URL:', API_URL);
    console.log('Full URL:', molecularUrl);
    console.log('Payload:', payload);
    console.log('============================');

    addLog(`📤 URL: ${molecularUrl}`);

    try {
      setProgress(35);

      const response = await axios.post(
        molecularUrl,
        payload,
        {
          headers: {
            'X-CTF-API-Key': API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      setProgress(100);
      const data = response.data;
      setResult(data);

      setAnalysisHistory((previous) =>
        [
          {
            id: Date.now(),
            patientId: patientId.trim(),
            timestamp: new Date().toISOString(),
            result: data,
          },
          ...previous,
        ].slice(0, 10)
      );

      addLog('✅ Molecular analysis complete');
      addLog(
        `🎯 Prediction: ${data.predicted_label || 'Unknown'} — confidence: ${(
          Number(data.confidence || 0) * 100
        ).toFixed(1)}%`
      );
    } catch (requestError) {
      console.error('===== MOLECULAR ERROR =====');
      console.error('URL:', molecularUrl);
      console.error('Payload:', payload);
      console.error('Error:', requestError);
      console.error('Response:', requestError.response);
      console.error('===========================');

      const message =
        requestError.response?.data?.detail ||
        requestError.message ||
        'Molecular analysis failed.';

      setError(`${message} (URL: ${molecularUrl})`);
      addLog(`❌ Error: ${message}`);
      if (requestError.response?.status === 405) {
        addLog(`⚠️ 405 Method Not Allowed — URL: ${molecularUrl}`);
      }
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [patientId, manualFeatureValues, addLog]);

  const handleBatchAnalyze = useCallback(async () => {
    if (!selectedFile || fileRows.length === 0) {
      setError('Please select a valid CSV file.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setBatchResults([]);
    setProgress(10);

    addLog('🚀 Starting batch molecular analysis...');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      setProgress(35);
      const batchUrl = `${API_URL}/v1/predict/molecular/batch/`;
      addLog(`📤 Uploading to: ${batchUrl}`);

      console.log('===== MOLECULAR BATCH REQUEST =====');
      console.log('URL:', batchUrl);
      console.log('File:', selectedFile.name);
      console.log('===================================');

      const response = await axios.post(
        batchUrl,
        formData,
        {
          headers: {
            'X-CTF-API-Key': API_KEY,
          },
        }
      );

      setProgress(100);
      const data = response.data;
      const results = Array.isArray(data) ? data : data.results || [];
      setBatchResults(results);

      addLog(`✅ Batch analysis completed for ${results.length} record(s)`);
    } catch (requestError) {
      console.error('===== MOLECULAR BATCH ERROR =====');
      console.error('Error:', requestError);
      console.error('==================================');

      const message =
        requestError.response?.data?.detail ||
        requestError.message ||
        'Batch molecular analysis failed.';

      setError(message);
      addLog(`❌ Error: ${message}`);
      if (requestError.response?.status === 405) {
        addLog('⚠️ 405 Method Not Allowed — check API_URL');
      }
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [selectedFile, fileRows.length, addLog]);

  const resetAnalysis = useCallback(() => {
    setPatientId('');
    setFeatures(createInitialFeatures());
    setSelectedFile(null);
    setFileRows([]);
    setFileHeaders([]);
    setResult(null);
    setBatchResults([]);
    setError(null);
    setProgress(0);
    setLogs([]);
    addLog('🔄 Molecular analysis reset');
  }, [addLog]);

  const handleDownload = useCallback(() => {
    const output = batchResults.length > 0 ? batchResults : result;
    if (!output) return;

    const blob = new Blob(
      [JSON.stringify(output, null, 2)],
      { type: 'application/json' }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      batchResults.length > 0
        ? 'molecular_batch_results.json'
        : `molecular_result_${patientId || 'unknown'}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [result, batchResults, patientId]);

  const renderManualInputs = () => (
    <>
      <TextField
        fullWidth
        label="Patient ID"
        value={patientId}
        onChange={(event) => setPatientId(event.target.value)}
        sx={{ mb: 3 }}
      />

      <Grid container spacing={2}>
        {MOLECULAR_FEATURES.map((feature) => (
          <Grid item xs={12} sm={6} key={feature.key}>
            {feature.type === 'select' ? (
              <TextField
                select
                fullWidth
                size="small"
                label={feature.label}
                value={features[feature.key]}
                onChange={(event) =>
                  handleFeatureChange(feature.key, event.target.value)
                }
              >
                {feature.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                size="small"
                type="number"
                label={feature.label}
                value={features[feature.key]}
                onChange={(event) =>
                  handleFeatureChange(feature.key, event.target.value)
                }
                inputProps={{
                  min: feature.min,
                  max: feature.max,
                  step: feature.step,
                }}
              />
            )}
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleManualAnalyze}
          disabled={isLoading || !patientId.trim()}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AnalyzeIcon />
            )
          }
        >
          {isLoading ? 'Analyzing...' : 'Run Molecular Analysis'}
        </Button>

        <Button
          variant="outlined"
          onClick={resetAnalysis}
          disabled={isLoading}
          startIcon={<RefreshIcon />}
        >
          Reset
        </Button>
      </Box>
    </>
  );

  const renderFileUpload = () => (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          borderStyle: 'dashed',
          bgcolor: 'background.default',
        }}
      >
        <MolecularIcon sx={{ fontSize: 52, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Upload Molecular Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload structured GeneXpert, PCR, mutation, resistance, biomarker, or molecular-test results.
        </Typography>

        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon />}
          disabled={isLoading}
        >
          Select CSV File
          <input
            type="file"
            hidden
            accept=".csv,text/csv"
            onChange={handleFileSelection}
          />
        </Button>

        {selectedFile && (
          <Box sx={{ mt: 2 }}>
            <Chip
              color="primary"
              variant="outlined"
              label={`${selectedFile.name} · ${fileRows.length} rows`}
            />
          </Box>
        )}
      </Paper>

      {fileRows.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Data Preview
          </Typography>
          <Box
            sx={{
              mt: 1,
              overflowX: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box
              component="table"
              sx={{
                minWidth: 800,
                width: '100%',
                borderCollapse: 'collapse',
                '& th, & td': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                  fontSize: 12,
                },
                '& th': {
                  bgcolor: 'action.hover',
                  fontWeight: 700,
                },
              }}
            >
              <thead>
                <tr>
                  {fileHeaders.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fileRows.slice(0, 5).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {fileHeaders.map((header) => (
                      <td key={`${rowIndex}-${header}`}>{row[header]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Showing first {Math.min(5, fileRows.length)} of {fileRows.length} rows.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleBatchAnalyze}
          disabled={isLoading || !selectedFile || fileRows.length === 0}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AnalyzeIcon />
            )
          }
        >
          {isLoading ? 'Analyzing...' : 'Run Batch Analysis'}
        </Button>

        <Button
          variant="outlined"
          onClick={resetAnalysis}
          disabled={isLoading}
          startIcon={<RefreshIcon />}
        >
          Reset
        </Button>
      </Box>
    </>
  );

  const renderSingleResult = () => {
    if (!result) return null;

    const confidence = Number(result.confidence || 0) * 100;
    const molecularProbability =
      Number(result.molecular_probability ?? result.tb_probability ?? 0) * 100;

    return (
      <Fade in timeout={500}>
        <Box>
          <Card
            sx={{
              mb: 3,
              bgcolor:
                result.predicted_label === 'TB' ? 'error.light' : 'success.light',
            }}
          >
            <CardContent>
              <Typography variant="h5" gutterBottom>
                🧬 Prediction: {result.predicted_label || 'Unknown'}
              </Typography>
              <Typography variant="h6">
                Molecular Probability: {molecularProbability.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, molecularProbability))}
                sx={{ height: 12, borderRadius: 6, mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence: {confidence.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Encoder: {result.encoder_version || 'Molecular encoder'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Embedding dimension: {result.embedding_dimension || 128}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {result.clinically_valid === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This molecular checkpoint has not yet been clinically validated.
            </Alert>
          )}

          {Array.isArray(result.resistance_markers) &&
            result.resistance_markers.length > 0 && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Resistance Markers
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {result.resistance_markers.map((marker) => (
                      <Chip key={marker} label={marker} color="warning" variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

          <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download Result JSON
          </Button>
        </Box>
      </Fade>
    );
  };

  const renderBatchResults = () => {
    if (batchResults.length === 0) return null;

    return (
      <Fade in timeout={500}>
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Batch analysis completed for {batchResults.length} record(s).
          </Alert>
          <Box sx={{ maxHeight: 430, overflow: 'auto' }}>
            {batchResults.map((entry, index) => (
              <Paper key={entry.patient_id || index} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography variant="body2" fontWeight="bold">
                  Patient: {entry.patient_id || `Row ${index + 1}`}
                </Typography>
                <Typography variant="body2">Prediction: {entry.predicted_label || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Confidence: {(Number(entry.confidence || 0) * 100).toFixed(1)}%
                </Typography>
              </Paper>
            ))}
          </Box>
          <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ mt: 2 }}>
            Download Batch Results
          </Button>
        </Box>
      </Fade>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🧬 Molecular Diagnostics Lab
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Analyze structured GeneXpert, PCR, biomarker, mutation, and resistance-test data
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/platform')}>
            Back to Platform
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Molecular Diagnostic Inputs
              </Typography>
              <Tabs
                value={inputMode}
                onChange={(_, value) => {
                  setInputMode(value);
                  setError(null);
                  setResult(null);
                  setBatchResults([]);
                }}
                sx={{ mb: 3 }}
              >
                <Tab value="manual" label="Manual Entry" />
                <Tab value="file" label="CSV Upload" />
              </Tabs>

              {inputMode === 'manual' ? renderManualInputs() : renderFileUpload()}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {isLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {Math.round(progress)}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)}>
                  <Tab label="Results" />
                  <Tab label="Logs" />
                </Tabs>
              </Box>

              <TabPanel value={selectedTab} index={0}>
                {result ? (
                  renderSingleResult()
                ) : batchResults.length > 0 ? (
                  renderBatchResults()
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 330,
                      textAlign: 'center',
                    }}
                  >
                    <MolecularIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                      Enter molecular features or upload a CSV file, then run analysis.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Molecular diagnostic results will appear here.
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No logs yet. Run analysis to see the processing workflow.
                    </Typography>
                  ) : (
                    logs.map((log, index) => (
                      <Box
                        key={`${log.timestamp}-${index}`}
                        sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>
            </CardContent>
          </Card>

          {analysisHistory.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Molecular Analysis History
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {analysisHistory.map((entry) => (
                    <Box key={entry.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {entry.patientId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Prediction: {entry.result.predicted_label || 'Unknown'} | Confidence:{' '}
                        {(Number(entry.result.confidence || 0) * 100).toFixed(2)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default MolecularAnalysis;
